import React, { useState, useEffect } from 'react';
import { Dialog, Typography, IconButton, Box, Button, Checkbox, Avatar, Radio } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ReplayIcon from '@mui/icons-material/Replay';
import { get, post } from '../services/api-services';

const CheckboxStyle = { color: '#333', '&.Mui-checked': { color: '#3c50b4' } };
const btnBack = { borderColor: '#333', color: '#fff', textTransform: 'none', borderRadius: '8px', '&:hover': { borderColor: '#555' } };
const btnNext = { backgroundColor: '#3c50b4', color: '#fff', textTransform: 'none', borderRadius: '8px', '&:hover': { backgroundColor: '#4f5b93' } };

const getTodayString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const SearchBar = ({ value, onChange }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#111', border: '1px solid #333', borderRadius: '8px', px: 2, mb: 2.5 }}>
        <SearchIcon sx={{ color: '#666', fontSize: 20, mr: 1 }} />
        <input
            type="text"
            placeholder="Search"
            value={value}
            onChange={onChange}
            style={{ width: '100%', padding: '10px 0', backgroundColor: 'transparent', border: 'none', outline: 'none', color: '#fff' }}
        />
    </Box>
);

const NavButtons = ({ onBack, onNext, nextLabel = 'Next' }) => (
    <Box mt={3} display="flex" gap={2}>
        <Button variant="outlined" onClick={onBack} fullWidth sx={btnBack}>Back</Button>
        <Button variant="contained" onClick={onNext} fullWidth sx={btnNext}>{nextLabel}</Button>
    </Box>
);

export default function AssignTaskDialog({ open, onClose, task, onRefresh }) {
    // steps: 1=type, 2=users or programs, 2b=program-users, 3=configure
    const [step, setStep] = useState(1);
    const [assignType, setAssignType] = useState('multiple');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedPrograms, setSelectedPrograms] = useState([]);
    const [selectedProgramForUsers, setSelectedProgramForUsers] = useState(null);

    const [allUsers, setAllUsers] = useState([]);
    const [allPrograms, setAllPrograms] = useState([]);
    const [programUsers, setProgramUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    // Config state
    const [duration, setDuration] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [errors, setErrors] = useState({});

    const fetchUsersAndPrograms = async () => {
        setLoading(true);
        try {
            const usersRes = await get('/Customers');
            if (usersRes?.data) setAllUsers(usersRes.data);

            const progsRes = await get('/Programs');
            if (progsRes?.data) setAllPrograms(progsRes.data);
        } catch (error) {
            console.error("Error fetching assignment data:", error);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (open) {
            setStep(1);
            setAssignType('multiple');
            setSearchQuery('');
            setSelectedUsers([]);
            setSelectedPrograms([]);
            setSelectedProgramForUsers(null);
            setFromDate('');
            setToDate('');
            setErrors({});
            if (task && task.targetValue) {
                setDuration(task.targetValue);
            } else {
                setDuration('');
            }
            fetchUsersAndPrograms();
        }
    }, [open, task]);

    const handleNext = async () => {
        if (step === 1) {
            setErrors({});
            setSearchQuery('');
            setStep(2);
        } else if (step === 2 && assignType === 'programs') {
            if (selectedPrograms.length === 0) {
                setErrors({ programs: 'Please select at least one program' });
                return;
            }
            setErrors({});
            setLoading(true);
            // Fetch users in the selected programs
            // For simplicity, fetch all UserPrograms for these programs
            const progId = selectedPrograms[0];
            const res = await get(`/UserPrograms?filter[where][programId]=${progId}&filter[include]=customer`);
            if (res?.data) {
                setProgramUsers(res.data.map(up => up.customer).filter(Boolean));
                setSelectedProgramForUsers(allPrograms.find(p => p.id === progId));
            }
            setLoading(false);
            setSelectedUsers([]);
            setSearchQuery('');
            setStep('2b');
        } else if (step === 2 && assignType !== 'programs') {
            if (selectedUsers.length === 0) {
                setErrors({ users: 'Please select at least one user' });
                return;
            }
            setErrors({});
            setStep(3);
        } else if (step === '2b') {
            if (selectedUsers.length === 0) {
                setErrors({ users: 'Please select at least one user' });
                return;
            }
            setErrors({});
            setStep(3);
        } else if (step === 3) {
            const newErrors = {};
            if (!duration || !String(duration).trim()) {
                newErrors.duration = 'Target Value is required';
            } else if (Number(duration) <= 0 || isNaN(Number(duration))) {
                newErrors.duration = 'Target Value must be a positive number';
            }

            if (!fromDate) {
                newErrors.fromDate = 'From Date is required';
            }

            if (fromDate && toDate && new Date(toDate) < new Date(fromDate)) {
                newErrors.toDate = 'To Date cannot be before From Date';
            }

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }

            setErrors({});
            setLoading(true);
            try {
                // Save assignments for each selected user
                for (const userId of selectedUsers) {
                    // Normalize date to UTC midnight for consistency with getActiveProgram
                    let assignmentDate = fromDate ? new Date(fromDate) : new Date();
                    assignmentDate.setHours(0, 0, 0, 0);
                    let assignmentEndDate = toDate ? new Date(toDate) : null;
                    if (assignmentEndDate) assignmentEndDate.setHours(23, 59, 59, 999);
                    
                    await post('/TaskLogs', {
                        userId: userId,
                        taskId: task.id,
                        date: assignmentDate,
                        endDate: assignmentEndDate,
                        status: 'pending',
                        value: duration
                    });
                }
                if (onRefresh) onRefresh();
                onClose();
            } catch (error) {
                console.error("Error saving assignments:", error);
            }
            setLoading(false);
        }
    };

    const handleBack = () => {
        setErrors({});
        if (step === 2) setStep(1);
        else if (step === '2b') { setStep(2); setSearchQuery(''); }
        else if (step === 3) setStep(assignType === 'programs' ? '2b' : 2);
    };

    const filteredUsers = allUsers.filter(u =>
        (u?.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (u?.email?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    );

    const filteredPrograms = allPrograms.filter(p =>
        (p?.title?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    );

    const toggleUser = (id) => {
        if (errors.users) setErrors(prev => ({ ...prev, users: '' }));
        if (assignType === 'individual') {
            // Radio: only one at a time
            setSelectedUsers([id]);
        } else {
            setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        }
    };

    const toggleProgram = (id) => {
        if (errors.programs) setErrors(prev => ({ ...prev, programs: '' }));
        setSelectedPrograms(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleAllUsers = () => {
        if (errors.users) setErrors(prev => ({ ...prev, users: '' }));
        setSelectedUsers(prev => prev.length === allUsers.length ? [] : allUsers.map(u => u.id));
    };

    const toggleAllPrograms = () => {
        if (errors.programs) setErrors(prev => ({ ...prev, programs: '' }));
        setSelectedPrograms(prev => prev.length === allPrograms.length ? [] : allPrograms.map(p => p.id));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    backgroundColor: '#1a1a1a',
                    color: '#fff',
                    width: '460px',
                    borderRadius: '12px',
                    border: '1px solid #333'
                }
            }}
        >
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" p={3} pb={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                    Assign Task: {task?.title || 'Task'}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ color: '#888', '&:hover': { color: '#fff', backgroundColor: '#333' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box p={3} pt={1} pb={4}>

                {/* ───── STEP 1: Assign Type ───── */}
                {step === 1 && (
                    <Box>
                        <Typography variant="caption" sx={{ color: '#aaa', mb: 2, display: 'block' }}>Assign to:</Typography>
                        <Box display="flex" gap={1.5}>
                            {[
                                { label: 'Individual', val: 'individual' },
                                { label: 'Multiple Individuals', val: 'multiple' },
                                { label: 'Programs', val: 'programs' },
                            ].map(({ label, val }) => {
                                const isSelected = assignType === val;
                                return (
                                    <Button
                                        key={val}
                                        onClick={() => setAssignType(val)}
                                        sx={{
                                            flex: 1,
                                            border: `1px solid ${isSelected ? '#3c50b4' : '#333'}`,
                                            backgroundColor: isSelected ? 'rgba(92, 107, 192, 0.1)' : 'transparent',
                                            color: isSelected ? '#fff' : '#aaa',
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            py: 1.5,
                                            fontSize: '0.78rem',
                                            '&:hover': { borderColor: '#3c50b4', color: '#fff' }
                                        }}
                                    >
                                        {label}
                                    </Button>
                                );
                            })}
                        </Box>
                        <Box mt={4}>
                            <Button variant="contained" fullWidth onClick={handleNext}
                                sx={{ backgroundColor: '#3c50b4', color: '#fff', py: 1.2, textTransform: 'none', fontWeight: 'bold', '&:hover': { backgroundColor: '#4f5b93' }, borderRadius: '8px' }}>
                                Next
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* ───── STEP 2a: Select Users (Individual / Multiple) ───── */}
                {step === 2 && assignType !== 'programs' && (
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="caption" sx={{ color: '#aaa' }}>
                                {assignType === 'individual' ? 'Select User' : 'Select Users'}
                            </Typography>
                            {/* Select All only for multiple */}
                            {assignType !== 'individual' && (
                                <Box display="flex" alignItems="center" onClick={toggleAllUsers} sx={{ cursor: 'pointer' }}>
                                    <Typography variant="caption" sx={{ color: '#aaa', mr: 1 }}>Select All</Typography>
                                    <Checkbox
                                        checked={selectedUsers.length === allUsers.length && allUsers.length > 0}
                                        size="small" sx={{ p: 0, ...CheckboxStyle }}
                                    />
                                </Box>
                            )}
                        </Box>
                        <SearchBar value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                        {errors.users && (
                            <Typography variant="caption" sx={{ color: '#e57373', mb: 2, display: 'block', fontWeight: 'bold' }}>
                                {errors.users}
                            </Typography>
                        )}
                        <Box sx={{ maxHeight: '250px', overflowY: 'auto', pr: 1 }}>
                            {filteredUsers.map((user) => (
                                <Box
                                    key={user.id}
                                    display="flex" justifyContent="space-between" alignItems="center" mb={2}
                                    onClick={() => toggleUser(user.id)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <Avatar sx={{ width: 32, height: 32, backgroundColor: '#333', fontSize: '0.8rem' }}>{user.name?.charAt(0) || 'U'}</Avatar>
                                        <Box>
                                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{user.name || 'Unknown User'}</Typography>
                                            <Typography variant="caption" sx={{ color: '#777', fontSize: '0.75rem' }}>{user.email || ''}</Typography>
                                        </Box>
                                    </Box>
                                    {assignType === 'individual' ? (
                                        <Radio
                                            checked={selectedUsers.includes(user.id)}
                                            onChange={() => {}}
                                            size="small"
                                            sx={{ color: '#333', '&.Mui-checked': { color: '#3c50b4' } }}
                                        />
                                    ) : (
                                        <Checkbox
                                            checked={selectedUsers.includes(user.id)}
                                            onChange={() => {}}
                                            size="small"
                                            sx={CheckboxStyle}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Box>
                        <NavButtons onBack={handleBack} onNext={handleNext} />
                    </Box>
                )}

                {/* ───── STEP 2b: Select Programs ───── */}
                {step === 2 && assignType === 'programs' && (
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="caption" sx={{ color: '#aaa' }}>Select Programs</Typography>
                            <Box display="flex" alignItems="center" onClick={toggleAllPrograms} sx={{ cursor: 'pointer' }}>
                                <Typography variant="caption" sx={{ color: '#aaa', mr: 1 }}>Select All</Typography>
                                <Checkbox
                                    checked={selectedPrograms.length === allPrograms.length && allPrograms.length > 0}
                                    size="small" sx={{ p: 0, ...CheckboxStyle }}
                                />
                            </Box>
                        </Box>
                        <SearchBar value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                        {errors.programs && (
                            <Typography variant="caption" sx={{ color: '#e57373', mb: 2, display: 'block', fontWeight: 'bold' }}>
                                {errors.programs}
                            </Typography>
                        )}
                        <Box sx={{ maxHeight: '260px', overflowY: 'auto', pr: 1 }}>
                            {filteredPrograms.map((prog) => (
                                <Box
                                    key={prog.id}
                                    display="flex" justifyContent="space-between" alignItems="center" mb={2}
                                    onClick={() => toggleProgram(prog.id)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <Avatar sx={{ width: 32, height: 32, backgroundColor: prog.color || '#3c50b4', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                            {prog.title?.charAt(0) || 'P'}
                                        </Avatar>
                                        <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.85rem' }}>{prog.title || 'Untitled Program'}</Typography>
                                    </Box>
                                    <Checkbox
                                        checked={selectedPrograms.includes(prog.id)}
                                        onChange={() => {}}
                                        size="small"
                                        sx={CheckboxStyle}
                                    />
                                </Box>
                            ))}
                        </Box>
                        <NavButtons onBack={handleBack} onNext={handleNext} />
                    </Box>
                )}

                {/* ───── STEP 2b: Users in selected program ───── */}
                {step === '2b' && (
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="caption" sx={{ color: '#aaa' }}>
                                Users in {selectedProgramForUsers?.name} ({selectedProgramForUsers?.userCount})
                            </Typography>
                            <Box display="flex" alignItems="center" onClick={toggleAllUsers} sx={{ cursor: 'pointer' }}>
                                <Typography variant="caption" sx={{ color: '#aaa', mr: 1 }}>Select All</Typography>
                                <Checkbox
                                    checked={selectedUsers.length === allUsers.length && allUsers.length > 0}
                                    size="small" sx={{ p: 0, ...CheckboxStyle }}
                                />
                            </Box>
                        </Box>
                        <SearchBar value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                        {errors.users && (
                            <Typography variant="caption" sx={{ color: '#e57373', mb: 2, display: 'block', fontWeight: 'bold' }}>
                                {errors.users}
                            </Typography>
                        )}
                        <Box sx={{ maxHeight: '250px', overflowY: 'auto', pr: 1 }}>
                            {programUsers.filter(u => (u?.name?.toLowerCase() || '').includes(searchQuery.toLowerCase())).map((user) => (
                                <Box
                                    key={user.id}
                                    display="flex" justifyContent="space-between" alignItems="center" mb={2}
                                    onClick={() => toggleUser(user.id)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <Avatar sx={{ width: 32, height: 32, backgroundColor: '#333', fontSize: '0.8rem' }}>{user.name?.charAt(0) || 'U'}</Avatar>
                                        <Box>
                                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{user.name || 'Unknown User'}</Typography>
                                            <Typography variant="caption" sx={{ color: '#777', fontSize: '0.75rem' }}>{user.email || ''}</Typography>
                                        </Box>
                                    </Box>
                                    <Checkbox
                                        checked={selectedUsers.includes(user.id)}
                                        onChange={() => {}}
                                        size="small"
                                        sx={CheckboxStyle}
                                    />
                                </Box>
                            ))}
                        </Box>
                        <NavButtons onBack={handleBack} onNext={handleNext} />
                    </Box>
                )}

                {/* ───── STEP 3: Configure Assignment ───── */}
                {step === 3 && (
                    <Box>
                        <Typography variant="caption" sx={{ color: '#aaa', mb: 2, display: 'block', fontWeight: 'bold' }}>Configure Assignment</Typography>

                        <Typography variant="caption" sx={{ color: errors.duration ? '#e57373' : '#888', mb: 0.5, display: 'block', fontWeight: errors.duration ? 'bold' : 'normal' }}>Target Value</Typography>
                        <Box display="flex" alignItems="center" gap={2} mb={errors.duration ? 1 : 3}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                backgroundColor: '#111', 
                                border: errors.duration ? '1px solid #e57373' : '1px solid #333', 
                                borderRadius: '8px', 
                                px: 2, 
                                flex: 1,
                                transition: 'border-color 0.2s ease',
                                '&:focus-within': { border: errors.duration ? '1px solid #e57373' : '1px solid #3c50b4' }
                            }}>
                                <input
                                    type="number"
                                    value={duration}
                                    onChange={(e) => {
                                        setDuration(e.target.value);
                                        if (errors.duration) setErrors(prev => ({ ...prev, duration: '' }));
                                    }}
                                    placeholder="45"
                                    style={{ width: '100%', padding: '10px 0', backgroundColor: 'transparent', border: 'none', outline: 'none', color: '#fff' }}
                                />
                                <Typography variant="caption" sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                                    {task?.taskType === 'workout' ? 'min' : 
                                     ['food', 'breakfast', 'lunch', 'dinner', 'diet'].includes(task?.taskType) ? 'gm' :
                                     ['water', 'beverage', 'beverages'].includes(task?.taskType) ? 'L' : ''}
                                </Typography>
                            </Box>
                            <Button startIcon={<ReplayIcon fontSize="small" />} onClick={() => {
                                setDuration('');
                                if (errors.duration) setErrors(prev => ({ ...prev, duration: '' }));
                            }}
                                sx={{ color: '#888', textTransform: 'none', minWidth: 'auto', px: 1, '&:hover': { color: '#fff' } }}>
                                Reset
                            </Button>
                        </Box>
                        {errors.duration && (
                            <Typography variant="caption" sx={{ color: '#e57373', mb: 2.5, display: 'block', fontSize: '0.75rem' }}>
                                {errors.duration}
                            </Typography>
                        )}

                        <Typography variant="caption" sx={{ color: (errors.fromDate || errors.toDate) ? '#e57373' : '#888', mb: 0.5, display: 'block', fontWeight: (errors.fromDate || errors.toDate) ? 'bold' : 'normal' }}>Schedule</Typography>
                        <Box display="flex" gap={2} mb={(errors.fromDate || errors.toDate) ? 1 : 3}>
                            <Box sx={{ 
                                flex: 1, 
                                backgroundColor: '#111', 
                                border: errors.fromDate ? '1px solid #e57373' : '1px solid #333', 
                                borderRadius: '8px', 
                                px: 2,
                                transition: 'border-color 0.2s ease',
                                '&:focus-within': { border: errors.fromDate ? '1px solid #e57373' : '1px solid #3c50b4' }
                            }}>
                                <input
                                    type="date"
                                    value={fromDate}
                                    min={getTodayString()}
                                    onChange={(e) => {
                                        setFromDate(e.target.value);
                                        if (errors.fromDate) setErrors(prev => ({ ...prev, fromDate: '' }));
                                        // If selected fromDate is later than toDate, clear toDate
                                        if (toDate && e.target.value > toDate) {
                                            setToDate('');
                                        }
                                    }}
                                    placeholder="From"
                                    style={{ width: '100%', padding: '10px 0', backgroundColor: 'transparent', border: 'none', outline: 'none', color: fromDate ? '#fff' : '#666', colorScheme: 'dark' }}
                                />
                            </Box>
                            <Box sx={{ 
                                flex: 1, 
                                backgroundColor: '#111', 
                                border: errors.toDate ? '1px solid #e57373' : '1px solid #333', 
                                borderRadius: '8px', 
                                px: 2,
                                transition: 'border-color 0.2s ease',
                                '&:focus-within': { border: errors.toDate ? '1px solid #3c50b4' : '1px solid #3c50b4' }
                            }}>
                                <input
                                    type="date"
                                    value={toDate}
                                    min={fromDate || getTodayString()}
                                    onChange={(e) => {
                                        setToDate(e.target.value);
                                        if (errors.toDate) setErrors(prev => ({ ...prev, toDate: '' }));
                                    }}
                                    placeholder="To"
                                    style={{ width: '100%', padding: '10px 0', backgroundColor: 'transparent', border: 'none', outline: 'none', color: toDate ? '#fff' : '#666', colorScheme: 'dark' }}
                                />
                            </Box>
                        </Box>
                        {errors.fromDate && (
                            <Typography variant="caption" sx={{ color: '#e57373', mb: 1, display: 'block', fontSize: '0.75rem' }}>
                                {errors.fromDate}
                            </Typography>
                        )}
                        {errors.toDate && (
                            <Typography variant="caption" sx={{ color: '#e57373', mb: 1, display: 'block', fontSize: '0.75rem' }}>
                                {errors.toDate}
                            </Typography>
                        )}

                        <Box sx={{ backgroundColor: 'rgba(92, 107, 192, 0.1)', border: '1px solid rgba(92, 107, 192, 0.3)', borderRadius: '8px', p: 2, mb: 4 }}>
                            <Typography variant="caption" sx={{ color: '#3c50b4', fontWeight: 'bold' }}>NOTE: </Typography>
                            <Typography variant="caption" sx={{ color: '#888' }}>The assignment starts on selected date. Ends on selected date or continues if not set.</Typography>
                        </Box>

                        <NavButtons onBack={handleBack} onNext={handleNext} nextLabel="Save changes" />
                    </Box>
                )}
            </Box>
        </Dialog>
    );
}
